"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, Eye, Loader2, Lock, MessageSquare } from "lucide-react";
import { Alert, AlertDescription } from "@/app/_module/components/ui/alert";
import { Button } from "@/app/_module/components/ui/button";
import { Skeleton } from "@/app/_module/components/ui/skeleton";
import { Dialog, DialogContent } from "@/app/_module/components/ui/dialog";
import { getSupabaseClient } from "@/app/_module/services/supabaseClient";
import { forumService, CommentSort, timeAgo, formatScore, forumPath } from "./ForumService";
import { VoteControl } from "./VoteControl";
import { AuthorName } from "./KarmaHovercard";
import { CategoryChip, LockedBadge, PinnedBadge } from "./ForumBadges";
import { ForumMarkdown } from "./ForumMarkdown";
import { CommentTree, CommentComposer } from "./CommentTree";
import { PostActionsMenu } from "./PostActionsMenu";
import { ForumEmptyState } from "./ForumChrome";
import { removeRealtimeChannel } from "../../../../_module/services/realtimeTeardown";
import { ThreadSummary } from "@/app/_module/components/forum/ThreadSummary";

// Full post view + live comment tree. Realtime: postgres_changes INSERT on
// forum_comments filtered by post_id invalidates the tree (team-chat pattern);
// the 60s refetchInterval is the fallback when realtime hiccups.
export function PostDetail({ idOrSlug, rootId }: { idOrSlug: string; rootId?: string }) {
  const [commentSort, setCommentSort] = useState<CommentSort>("top");
  const [lightbox, setLightbox] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const postQuery = useQuery({
    queryKey: ["forum", "post", idOrSlug],
    queryFn: () => forumService.getPost(idOrSlug),
    staleTime: 30_000,
  });
  const post = postQuery.data;

  // Count the view once per visit; refetches never bump (de-duplicated server-side).
  useEffect(() => {
    if (!post?.id) return;
    forumService.registerView(post.id).catch(() => {});
  }, [post?.id]);

  const commentsQuery = useQuery({
    queryKey: ["forum", "comments", post?.id, commentSort, rootId || null],
    queryFn: () => forumService.getComments(post!.id, { sort: commentSort, rootId: rootId || null }),
    enabled: !!post?.id,
    refetchInterval: 60_000,
    staleTime: 15_000,
  });

  // Realtime: new comments on THIS post → invalidate the tree.
  useEffect(() => {
    if (!post?.id) return;
    let channel: any;
    let cancelled = false;
    (async () => {
      try {
        const supabase = await getSupabaseClient();
        if (cancelled) return;
        channel = supabase
          .channel(`forum-post-${post.id}`)
          .on(
            "postgres_changes",
            { event: "INSERT", schema: "public", table: "forum_comments", filter: `post_id=eq.${post.id}` },
            () => {
              queryClient.invalidateQueries({ queryKey: ["forum", "comments", post.id] });
              queryClient.invalidateQueries({ queryKey: ["forum", "post", idOrSlug] });
            },
          )
          .subscribe();
      } catch {
        /* polling fallback covers it */
      }
    })();
    return () => {
      cancelled = true;
      removeRealtimeChannel(channel);
    };
  }, [post?.id, idOrSlug, queryClient]);

  if (postQuery.isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (postQuery.isError || !post) {
    return (
      <ForumEmptyState
        title="This post is gone"
        subtitle="It may have been removed or the link is wrong."
        action={
          <Button asChild size="sm" variant="outline">
            <Link href={forumPath()}>Back to the feed</Link>
          </Button>
        }
      />
    );
  }

  return (
    <div>
      <Link
        href={rootId ? forumPath(`/t/${post.slug}`) : forumPath()}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
        {rootId ? "Full discussion" : "Back to feed"}
      </Link>

      <div className="mt-2 rounded-lg border bg-card">
        <div className="flex gap-3 p-4 sm:p-5">
          <div className="shrink-0">
            <VoteControl targetType="post" targetId={post.id} score={post.score} myVote={post.myVote} disabled={post.isDeleted} />
          </div>
          <div className="min-w-0 flex-1 group">
            <div className="flex flex-wrap items-center gap-1.5">
              <CategoryChip name={post.category.name} slug={post.category.slug} />
              {post.isPinned && <PinnedBadge />}
              {post.isLocked && <LockedBadge />}
            </div>
            <h1 className="forum-serif mt-1 text-[22px] font-medium leading-snug text-foreground sm:text-[26px]">{post.title}</h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
              {post.author ? <AuthorName author={post.author} showAvatar /> : <span className="italic">[deleted]</span>}
              <span className="font-mono tabular-nums">{timeAgo(post.createdAt)}</span>
              {post.isEdited && <span className="italic">edited</span>}
              <span className="inline-flex items-center gap-1 font-mono tabular-nums">
                <Eye className="h-3 w-3" /> {formatScore(post.viewCount)}
              </span>
              {!post.isDeleted && (
                <span className="ml-auto">
                  <PostActionsMenu post={{ ...post, bodyPreview: "", imageUrl: null } as any} isOwn={post.isOwn} stayOnDelete />
                </span>
              )}
            </div>

            {post.body && post.isDeleted ? (
              <p className="mt-3 text-sm italic text-muted-foreground">{post.body}</p>
            ) : post.body ? (
              <ForumMarkdown className="mt-3">{post.body}</ForumMarkdown>
            ) : null}

            {post.images.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {post.images.map((img) => (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    key={img.url}
                    src={img.url}
                    alt=""
                    onClick={() => setLightbox(img.url)}
                    className="max-h-96 max-w-full rounded-lg border object-contain cursor-zoom-in"
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-3">
        <ThreadSummary
          surface="crm"
          title={post.title}
          body={post.isDeleted ? "" : post.body || ""}
          replies={(commentsQuery.data?.comments || []).filter((c) => !c.isRemoved && c.author).map((c) => ({ author: c.author!.name, text: c.body }))}
        />
      </div>

      <div className="mt-3">
        {post.isLocked ? (
          <Alert className="border-amber-300 bg-amber-50 dark:bg-amber-950/40">
            <Lock className="h-4 w-4" />
            <AlertDescription className="text-xs">
              This thread is locked — new comments are off, but you can still read and vote.
            </AlertDescription>
          </Alert>
        ) : (
          <CommentComposer postId={post.id} />
        )}
      </div>

      <div className="mt-4 flex items-center justify-between">
        <h2 className="forum-serif inline-flex items-center gap-1.5 text-[17px] font-medium">
          <MessageSquare className="h-4 w-4 text-muted-foreground" />
          {formatScore(commentsQuery.data?.totalCount ?? post.commentCount)} replies
        </h2>
        <div className="flex gap-1">
          {(["top", "new"] as CommentSort[]).map((s) => (
            <Button
              key={s}
              size="sm"
              variant={commentSort === s ? "secondary" : "ghost"}
              className="h-6 px-2 text-[11px] capitalize"
              onClick={() => setCommentSort(s)}
            >
              {s}
            </Button>
          ))}
        </div>
      </div>

      <div className="mt-2">
        {commentsQuery.isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {commentsQuery.data?.capped && (
              <p className="pb-2 text-[11px] text-muted-foreground">
                Showing the first {commentsQuery.data.comments.length} comments of a big thread.
              </p>
            )}
            <CommentTree
              comments={commentsQuery.data?.comments || []}
              postId={post.id}
              postSlug={post.slug}
              postLocked={post.isLocked}
              rootId={rootId || null}
            />
            {commentsQuery.data?.hasMore && !rootId && (
              <p className="pt-3 text-center text-[11px] text-muted-foreground">
                More top-level comments exist — refresh or switch sort to see them (pagination lands in v1.1).
              </p>
            )}
          </>
        )}
      </div>

      <Dialog open={!!lightbox} onOpenChange={(v) => !v && setLightbox(null)}>
        <DialogContent className="max-w-4xl p-1 bg-transparent border-0 shadow-none">
          {lightbox && (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={lightbox} alt="" className="w-full max-h-[85vh] object-contain rounded-lg" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
